import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, Calendar, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import SEO from "@/components/SEO";
import { api, V1 } from "@/lib/api";

const CATEGORIES = [
  { v: "all", l: "Tümü" },
  { v: "bursluluk", l: "Bursluluk" },
  { v: "lgs", l: "LGS" },
  { v: "tyt", l: "TYT" },
  { v: "ayt", l: "AYT" },
  { v: "il-rehberi", l: "İl Rehberleri" },
  { v: "genel", l: "Genel" },
];

export default function Blog() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const category = sp.get("kategori") || "all";
  const q = sp.get("q") || "";

  useEffect(() => {
    const p = new URLSearchParams();
    if (category !== "all") p.set("category", category);
    if (q) p.set("q", q);
    p.set("limit", "24");
    api.get(`${V1}/blog?${p.toString()}`).then((r) => setItems(r.data.items || []));
  }, [category, q]);

  const upd = (k, v) => {
    const n = new URLSearchParams(sp);
    if (v && v !== "all") n.set(k, v); else n.delete(k);
    setSp(n, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="blog-page">
      <SEO
        title="Blog"
        description="Bursluluk sınavı, LGS, TYT, AYT hazırlığı ve özel okul rehberleri. Uzman yazarlardan güncel içerikler."
        canonical="/blog"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "SanoStudent Blog",
          url: "https://bursluluksinavi.com/blog",
        }}
      />
      <div className="mb-10">
        <div className="overline text-accent mb-2">Blog</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Bursluluk & Sınav Rehberleri</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">Uzman yazarlardan güncel içerikler; LGS, TYT, AYT hazırlığı, tercih rehberleri ve özel okul kılavuzları.</p>
      </div>
      <div className="grid md:grid-cols-4 gap-3 mb-8 p-4 rounded-xl border border-border bg-card">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input placeholder="Yazı ara..." value={q} onChange={(e) => upd("q", e.target.value)} className="pl-9" data-testid="blog-search" />
        </div>
        <Select value={category} onValueChange={(v) => upd("kategori", v)}>
          <SelectTrigger data-testid="blog-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
        {items.length === 0 && <div className="col-span-3 p-16 text-center text-muted-foreground">Henüz yazı yok.</div>}
        {items.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
                className="group overflow-hidden rounded-xl border border-border bg-card hover:border-primary/40 flex flex-col"
                style={{ transition: "border-color 0.2s ease" }}>
            <div className="aspect-[16/9] bg-muted overflow-hidden">
              {p.cover_url ? <img src={p.cover_url} alt={p.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> :
                <div className="h-full w-full flex items-center justify-center bg-primary/5 text-primary text-sm">SanoStudent</div>}
            </div>
            <div className="p-5 flex-1 flex flex-col">
              <div className="text-[10px] uppercase tracking-widest text-accent mb-2">{p.category}</div>
              <div className="font-display font-bold text-lg leading-tight mb-2 line-clamp-2">{p.title}</div>
              <div className="text-sm text-muted-foreground line-clamp-2">{p.excerpt}</div>
              <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground pt-3 border-t border-border">
                <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {new Date(p.created_at).toLocaleDateString("tr-TR")}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read_minutes} dk okuma</span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
